import React from 'react';

/**
 * WebSummitBrief — Web Summit Startup Programme intro call one-pager.
 * Route: /hq/websummit (ProtectedRoute allowEmails — sadece sahibine açık)
 * Statik sunum sayfası: toplantıda ekran paylaşımıyla gösterilir. API çağrısı yok.
 *
 * METRICS içindeki `value: null` olanlar toplantı öncesi doldurulmalı.
 */

const METRICS = [
  { key: 'Founded',                value: null },
  { key: 'Brands on platform',     value: null },
  { key: 'ARR',                    value: null },
  { key: 'Ad spend managed / mo',  value: null },
  { key: 'Team size',              value: null },
  { key: 'Capital raised',         value: null },
];

const PILLARS = [
  {
    title: 'Unified data layer',
    body: 'Every ad platform, GA4, BigQuery and Merchant Center synced into a single per-brand model — down to product and SKU level.',
  },
  {
    title: 'AI that recommends, not just reports',
    body: 'GlowyAI surfaces budget drift, ROAS decay, feed disapprovals and stalled campaigns proactively, in plain language, with the action attached.',
  },
  {
    title: 'Built where the market is',
    body: "Live in the Shopify and ikas app stores, with integrations global tools don't build for the Türkiye and MENA commerce stack.",
  },
];

const OBJECTIVES = [
  {
    title: 'First ten international customers',
    body: "We're proven in our home market. The goal for Lisbon is a qualified pipeline of mid-market European e-commerce brands and the agencies that serve them.",
  },
  {
    title: 'Integration and channel partnerships',
    body: 'Beyond Shopify and ikas — commerce platforms, agency networks and data partners who can put Glowytics in front of their own merchant base.',
  },
  {
    title: 'Investor conversations for our next round',
    body: "We want to meet funds active in vertical SaaS and applied AI, and understand what the programme's investor matching realistically delivers.",
  },
];

const QUESTIONS = [
  'Which package tier fits a revenue-generating company at our stage, and what exactly does each include?',
  'How does investor matching work in practice — who initiates, and how many meetings does a typical startup get?',
  'How many MarTech and e-commerce SaaS companies exhibited last year, and at what stage?',
  'How many exhibiting hours and days does a stand cover, and what lead volume do your own numbers show?',
  'Is there a Türkiye pavilion, national programme or partner discount we should know about?',
  'What are the early-bird pricing terms and the decision deadline?',
];

const Eyebrow = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">{children}</span>
);

const SectionHead = ({ title, n }) => (
  <h2 className="flex items-baseline justify-between gap-4 border-b-2 border-slate-900 pb-2.5">
    <span className="font-serif text-[15px] font-semibold text-slate-900">{title}</span>
    <span className="font-mono text-[11px] tracking-[0.1em] text-amber-600">{n}</span>
  </h2>
);

export default function WebSummitBrief() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="mx-auto flex max-w-4xl flex-col gap-11">

        {/* Masthead */}
        <header className="flex flex-col gap-3.5">
          <Eyebrow>Company brief · Web Summit Startup Programme · Lisbon 2026</Eyebrow>
          <h1 className="m-0 font-serif text-5xl font-semibold tracking-tight text-slate-900">
            Glowytics<span className="text-amber-600">.</span>
          </h1>
          <p className="m-0 max-w-[34em] font-serif text-[21px] leading-snug text-slate-600">
            Marketing intelligence for multi-channel retail and e-commerce brands — one platform that
            unifies ad, analytics and product data, then tells operators what to do next.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-200 pt-2.5">
            <Eyebrow>Category — MarTech / B2B SaaS</Eyebrow>
            <Eyebrow>HQ — Istanbul, Türkiye</Eyebrow>
            <Eyebrow>Contact — efe@glowytics.com</Eyebrow>
          </div>
        </header>

        {/* 01 — What we do */}
        <section className="flex flex-col gap-4">
          <SectionHead title="What we do" n="01" />
          <p className="m-0 max-w-[62ch] text-[17px] leading-relaxed text-slate-700">
            Brands run ads across Google, Meta, TikTok and Pinterest, sell through Shopify and local
            commerce platforms, and measure in GA4 and BigQuery. The data never meets in one place, so
            decisions get made on yesterday's spreadsheet. Glowytics closes that gap — and goes one
            step further than reporting.
          </p>
          <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
            {PILLARS.map((p) => (
              <div key={p.title} className="flex flex-col gap-2 bg-white p-5">
                <h3 className="m-0 text-sm font-semibold text-slate-900">{p.title}</h3>
                <p className="m-0 text-sm leading-relaxed text-slate-600">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 02 — Where we are */}
        <section className="flex flex-col gap-4">
          <SectionHead title="Where we are" n="02" />
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
            {METRICS.map((m) => (
              <div key={m.key} className="flex flex-col gap-1">
                {m.value ? (
                  <span className="font-serif text-3xl font-semibold tabular-nums tracking-tight text-slate-900">
                    {m.value}
                  </span>
                ) : (
                  <span className="self-start border-b-2 border-dotted border-amber-500 pr-8 font-serif text-3xl font-semibold text-slate-300">
                    &nbsp;
                  </span>
                )}
                <span className="font-mono text-[10.5px] uppercase tracking-[0.11em] text-slate-400">
                  {m.key}
                </span>
              </div>
            ))}
          </div>
          <p className="m-0 border-l-[3px] border-amber-500 bg-amber-50 px-4 py-3.5 text-[14.5px] text-slate-600">
            <strong className="font-semibold text-slate-900">Selected brands using Glowytics:</strong>{' '}
            Koton · Yataş · Canon · Skechers · Playmobil — alongside performance agencies managing
            multi-brand portfolios.
          </p>
        </section>

        {/* 03 — Why Web Summit */}
        <section className="flex flex-col gap-4">
          <SectionHead title="Why we're looking at Web Summit" n="03" />
          <div className="flex flex-col">
            {OBJECTIVES.map((o, i) => (
              <div
                key={o.title}
                className="grid grid-cols-[34px_1fr] items-start gap-4 border-b border-slate-200 py-4 last:border-b-0"
              >
                <span className="pt-1 font-mono text-xs tracking-wider text-amber-600">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="m-0 mb-1 text-[15.5px] font-semibold text-slate-900">{o.title}</h3>
                  <p className="m-0 max-w-[60ch] text-[14.5px] leading-relaxed text-slate-600">{o.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 04 — Our questions */}
        <section className="flex flex-col gap-4">
          <SectionHead title="What we'd like to understand from you" n="04" />
          <ul className="m-0 grid list-none gap-x-8 p-0 sm:grid-cols-2">
            {QUESTIONS.map((q, i) => (
              <li
                key={q}
                className="grid grid-cols-[26px_1fr] gap-2.5 border-t border-slate-200 py-2.5 text-[14.5px] leading-relaxed text-slate-600"
              >
                <span className="pt-0.5 font-mono text-[10.5px] tracking-wide text-teal-600">Q{i + 1}</span>
                <span>{q}</span>
              </li>
            ))}
          </ul>
        </section>

        <footer className="flex flex-wrap justify-between gap-3 border-t-2 border-slate-900 pt-5">
          <Eyebrow>Glowytics · glowytics.com</Eyebrow>
          <Eyebrow>Prepared for Web Summit intro call · July 2026</Eyebrow>
        </footer>

      </div>
    </div>
  );
}
